import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import {
	useCreateLegalMatterPracticeAreaMutation,
	useLegalMatterPracticeAreaAutocompleteQuery,
} from '../../../../../queries';
import { SearchIcon } from '../../../svgs/icons/SearchIcon';
import { TextInput } from '../../../TextInput';
import { styleSheet } from '../../SearchComponent/styles';

export const MatterPracticeAreaSearch = ({
	onSelectPracticeArea,
	canAdd = true,
	onCreatePracticeArea,
	initialSearchFragment,
	className,
	dropdownClassName,
	onClear,
}: {
	onSelectPracticeArea: (practiceArea: Api.ILegalMatterPracticeArea) => void;
	canAdd?: boolean;
	onCreatePracticeArea?: (practiceArea: Api.ILegalMatterPracticeArea) => void;
	initialSearchFragment?: string;
	className?: string;
	dropdownClassName?: string;
	onClear?: () => void;
}) => {
	const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
	const [searchFragment, setSearchFragment] = React.useState<string>(initialSearchFragment ?? '');
	const addPracticeAreaMutation = useCreateLegalMatterPracticeAreaMutation({
		onSuccess: (result: Api.ILegalMatterPracticeArea) => {
			setSearchFragment(result.name);
			onCreatePracticeArea?.(result);
			setIsDropdownOpen(false);
		},
	});

	const selectPracticeArea = (practiceArea: Api.ILegalMatterPracticeArea) => {
		setSearchFragment(practiceArea.name);
		setIsDropdownOpen(false);
		onSelectPracticeArea(practiceArea);
	};

	const updateSearchFragment = (newValue: string) => {
		setSearchFragment(newValue);

		if (newValue) {
			setIsDropdownOpen(true);
		} else {
			setIsDropdownOpen(false);
			onClear?.();
		}
	};

	const createNewPracticeArea = () => {
		const practiceArea: Api.ILegalMatterPracticeArea = {
			name: searchFragment,
		};
		addPracticeAreaMutation.mutate({
			practiceArea,
		});
	};

	const practiceAreaAutocompleteQuery = useLegalMatterPracticeAreaAutocompleteQuery({
		enabled: !!searchFragment,
		pageSize: 25,
		fragment: searchFragment,
	});

	const practiceAreas = practiceAreaAutocompleteQuery.data?.pages.map(page => page.values).flat() ?? [];

	return (
		<div className={`${css(styleSheet.searchInputWrap)} ${className || ''}`}>
			<TextInput
				className={css(isDropdownOpen ? styleSheet.popoverOpen : null)}
				inputId='matter-modal-edit-practice-area'
				onChange={e => updateSearchFragment(e.target.value)}
				type='text'
				value={searchFragment}
				placeholder='Search'
				leftAccessory={<SearchIcon className={css(styleSheet.searchIcon)} />}
			/>

			{isDropdownOpen ? (
				<div className={`${css(styleSheet.dropdown)} ${dropdownClassName || ''}`}>
					{practiceAreas.length === 0 && !practiceAreaAutocompleteQuery.isPending ? <i>No matches found</i> : null}

					{practiceAreas.map(practiceArea => {
						return (
							<button onClick={() => selectPracticeArea(practiceArea)} key={`practiceArea-${practiceArea.id}`}>
								{practiceArea.name}
							</button>
						);
					})}

					{canAdd && !!searchFragment ? (
						<button className={css(styleSheet.addNewButton)} onClick={createNewPracticeArea}>
							<span>+ Add &quot;{searchFragment}&quot;</span>
						</button>
					) : null}
				</div>
			) : null}
		</div>
	);
};
